<template>
    <v-container fluid class="pa-8 bg-grey-lighten-5 h-100">
        <div class="d-flex justify-space-between align-center mb-6">
            <div>
                <h2 class="text-h4 font-weight-bold text-grey-darken-3">문서 관리</h2>
                <p class="text-subtitle-1 text-grey-darken-1 mt-1">RAG 시스템에 활용될 문서를 업로드하고 관리합니다.</p>
            </div>
        </div>

        <v-row>
            <!-- List Section -->
            <v-col cols="12">
                <v-card class="rounded-xl border-none h-100 shadow-soft" elevation="0" color="white">
                    <div class="pa-6 d-flex justify-space-between align-center">
                        <div class="d-flex align-center">
                            <v-avatar color="indigo-lighten-5" class="mr-4" size="48">
                                <v-icon color="indigo-darken-2" size="24">mdi-file-document-multiple</v-icon>
                            </v-avatar>
                            <div>
                                <h3 class="text-h6 font-weight-bold text-grey-darken-3">업로드된 문서</h3>
                                <span class="text-caption text-grey-darken-1">총 {{ documents.length }}개의 문서</span>
                            </div>
                        </div>
                        <div class="d-flex align-center">
                            <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="문서 검색" single-line
                                hide-details variant="outlined" density="compact"
                                style="width: 280px; border-radius: 12px;" bg-color="grey-lighten-5" color="indigo"
                                class="mr-3 search-field"></v-text-field>
                            <v-btn color="indigo-darken-2" prepend-icon="mdi-cloud-upload"
                                @click="showUploadDialog = true" elevation="2" height="40"
                                class="rounded-lg px-6 font-weight-bold">
                                문서 업로드
                            </v-btn>
                        </div>
                    </div>

                    <v-divider class="border-opacity-50"></v-divider>

                    <v-data-table :headers="headers" :items="documents" :search="search" :loading="loading"
                        class="pa-2 modern-table" hover :items-per-page="10">
                        <template v-slot:[`item.cat_id`]="{ item }">
                            <v-chip size="small" variant="tonal" color="indigo" class="font-weight-medium">
                                {{ getCategoryName(item.raw.cat_id) }}
                            </v-chip>
                        </template>
                        <template v-slot:[titleSlot]="{ item }">
                            <div class="d-flex align-center py-3" @click="openDocument(item.raw)"
                                style="cursor: pointer;">
                                <v-avatar size="32" color="red-lighten-5" class="mr-3">
                                    <v-icon color="red-darken-1" size="18">mdi-file-pdf-box</v-icon>
                                </v-avatar>
                                <span class="font-weight-medium text-grey-darken-3 text-body-1">{{ item.raw.doc_title
                                    }}</span>
                            </div>
                        </template>
                        <template v-slot:[actionSlot]="{ item }">
                            <v-btn icon="mdi-delete-outline" variant="text" color="red-darken-2"
                                @click.stop="deleteDoc(item.raw)" :loading="loading"></v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <!-- Upload Dialog -->
        <v-dialog v-model="showUploadDialog" width="50%" persistent>
            <v-card class="rounded-xl shadow-soft" style="height: 80vh; min-height: 700px;">
                <v-card-title class="d-flex justify-space-between align-center pa-5 bg-grey-lighten-5">
                    <span class="text-h6 font-weight-bold text-grey-darken-3">문서 업로드</span>
                    <v-btn icon="mdi-close" variant="text" density="comfortable"
                        @click="showUploadDialog = false"></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-6">
                    <!-- Category Selection Section -->
                    <div class="mb-6 pa-4 bg-indigo-lighten-5 rounded-xl">
                        <div class="text-subtitle-2 font-weight-bold text-indigo-darken-4 mb-3 d-flex align-center">
                            <v-icon size="small" class="mr-2">mdi-tag-multiple</v-icon>
                            문서 분류 선택
                        </div>
                        <v-row dense>
                            <v-col cols="4">
                                <v-select v-model="selectedL1" :items="l1List" item-title="catNm" item-value="catId"
                                    label="대분류" variant="solo" flat density="comfortable" hide-details rounded="lg"
                                    @update:model-value="selectedL2 = null; selectedL3 = null"
                                    placeholder="대분류 선택"></v-select>
                            </v-col>
                            <v-col cols="4">
                                <v-select v-model="selectedL2" :items="l2List" item-title="catNm" item-value="catId"
                                    label="중분류" variant="solo" flat density="comfortable" hide-details rounded="lg"
                                    :disabled="!selectedL1" @update:model-value="selectedL3 = null"
                                    placeholder="중분류 선택"></v-select>
                            </v-col>
                            <v-col cols="4">
                                <v-select v-model="selectedL3" :items="l3List" item-title="catNm" item-value="catId"
                                    label="소분류" variant="solo" flat density="comfortable" hide-details rounded="lg"
                                    :disabled="!selectedL2" placeholder="소분류 선택"></v-select>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Advanced Options Section -->
                    <div class="mb-6 pa-4 bg-blue-grey-lighten-5 rounded-xl">
                        <div class="text-subtitle-2 font-weight-bold text-blue-grey-darken-4 mb-3 d-flex align-center">
                            <v-icon size="small" class="mr-2">mdi-cog-outline</v-icon>
                            고급 설정
                        </div>
                        <v-row dense>
                            <v-col cols="5">
                                <div class="d-flex align-center mb-1 ml-1">
                                    <span class="text-caption font-weight-bold text-blue-grey-darken-3">유사도 설정</span>
                                    <v-tooltip location="right" content-class="help-tooltip-dark">
                                        <template v-slot:activator="{ props }">
                                            <v-icon v-bind="props" size="14" color="blue-grey-lighten-2" class="ml-1 cursor-help">
                                                mdi-help-circle-outline
                                            </v-icon>
                                        </template>
                                        <!-- <span>유사도가 높을수록 파일 내용을 더 많이 참고하여 답변합니다.</span> -->
                                        <span>유사도가 높을수록 정말 같은 내용만, 낮을수록 비슷한 내용까지 함께 봅니다.</span>
                                    </v-tooltip>
                                </div>
                                <v-text-field v-model="uploadCosSim" variant="solo" flat density="comfortable"
                                    rounded="lg" type="number" step="0.001" min="0.1" max="1" placeholder="기본값 0.600"
                                    class="no-spin" :rules="[
                                        v => (v !== null && v !== undefined && String(v).trim() !== '') || '0.1 ~ 1.0 사이의 값을 입력하세요',
                                        v => (v >= 0.1 && v <= 1.0) || '0.1 ~ 1.0 사이의 값을 입력하세요',
                                        v => !String(v).includes('.') || String(v).split('.')[1].length <= 3 || '소수점 3자리까지만 허용됩니다'
                                    ]" @update:model-value="val => {
                                        const num = parseFloat(val);
                                        const decimalPart = String(val).split('.')[1];
                                        isSimValid = (num >= 0.1 && num <= 1.0) && (!decimalPart || decimalPart.length <= 3);
                                    }"></v-text-field>
                            </v-col>
                            <v-col cols="5" v-if="showHybridOption" class="ml-auto">
                                <div class="d-flex align-center mb-1 ml-1">
                                    <span class="text-caption font-weight-bold text-blue-grey-darken-3">하이브리드 파싱 (AI)</span>
                                    <v-tooltip location="right" content-class="help-tooltip-dark">
                                        <template v-slot:activator="{ props }">
                                            <v-icon v-bind="props" size="14" color="blue-grey-lighten-2" class="ml-1 cursor-help">
                                                mdi-help-circle-outline
                                            </v-icon>
                                        </template>
                                        <span>- 활성화시 파싱에 OCR을 사용합니다.</span><br>
                                        <span>- 이미지 형태의 문서를 파싱할 수 있습니다.</span><br>
                                        <span>- 문자를 잘못 인식할 수있습니다.(예: 휘발유->취발유)</span><br>
                                        <span>- OCR처리는 많은 시간이 소요됩니다.</span>
                                    </v-tooltip>
                                </div>
                                <v-switch v-model="useHybrid" color="indigo" inset density="comfortable" hide-details class="mt-n1"></v-switch>
                            </v-col>
                        </v-row>
                    </div>

                    <v-sheet class="pa-8 border-dashed rounded-xl bg-grey-lighten-5 d-flex align-center"
                        style="border: 2px dashed #9FA8DA; min-height: 180px; transition: all 0.3s;"
                        :class="{ 'bg-indigo-lighten-5': isDragging }" @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">

                        <v-row align="center" class="fill-height w-100 ma-0">
                            <!-- Left: Upload Info (3/4) -->
                            <v-col cols="9">
                                <div class="d-flex align-center">
                                    <v-avatar color="indigo-lighten-5" size="64" class="mr-6">
                                        <v-icon size="32" color="indigo-lighten-1">mdi-cloud-upload-outline</v-icon>
                                    </v-avatar>
                                    <div>
                                        <div class="text-h6 font-weight-bold text-grey-darken-2 mb-1">PDF 파일을 이곳에 놓으세요
                                        </div>
                                        <div class="text-body-2 text-grey-darken-1">파일을 드래그하거나 오른쪽 버튼을 클릭하여 선택하세요</div>
                                    </div>
                                </div>

                                <!-- Selected File Display (Inside Info Column) -->
                                <div v-if="files.length > 0" class="mt-4 pt-4 border-top">
                                    <div class="d-flex align-center bg-white pa-3 rounded-lg border mx-6">
                                        <v-icon color="red-darken-1" size="small" class="mr-3">mdi-file-pdf-box</v-icon>
                                        <span class="text-body-2 text-grey-darken-3 font-weight-medium text-truncate"
                                            style="max-width: 320px;">
                                            {{ files[0].name }}
                                        </span>
                                        <v-spacer></v-spacer>
                                        <v-btn icon="mdi-close" variant="text" density="compact" size="small"
                                            color="grey-darken-1" @click="files = []"></v-btn>
                                    </div>
                                </div>
                            </v-col>

                            <!-- Right: Action Buttons (1/4) -->
                            <v-col cols="3" class="d-flex flex-column justify-center ga-3">
                                <v-file-input v-model="files" accept=".pdf" label="파일 선택" variant="outlined"
                                    density="compact" prepend-icon="" hide-details class="d-none" :loading="uploading"
                                    @change="handleFileUpload" color="indigo" ref="fileInput"></v-file-input>

                                <v-btn color="indigo-darken-2" @click="$refs.fileInput.click()" variant="outlined"
                                    class="rounded-lg px-6 font-weight-bold mb-3" height="44" block
                                    :disabled="files.length > 0 || uploading">
                                    <v-icon class="mr-2">mdi-plus</v-icon>
                                    파일 선택
                                </v-btn>

                                <v-btn color="indigo-darken-2" @click="uploadFiles" :loading="uploading" elevation="2"
                                    height="44" class="rounded-lg font-weight-bold" block
                                    :disabled="files.length === 0 || uploading || !isSimValid">
                                    <v-icon class="mr-2">mdi-upload</v-icon>
                                    업로드
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-sheet>

                    <div class="mt-4 text-caption text-grey-darken-1 text-center">
                        <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
                        PDF 파일만 업로드 가능합니다.
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Document Detail Dialog -->
        <v-dialog v-model="showDialog" width="80%" scrollable persistent>
            <v-card class="rounded-xl shadow-soft" style="height: 80vh; min-height: 700px;">
                <v-card-title class="d-flex justify-space-between align-center pa-6 bg-grey-lighten-5 border-bottom">
                    <span class="text-h5 font-weight-bold text-grey-darken-3">{{ selectedDocument?.title }}</span>
                    <v-btn icon="mdi-close" variant="text" density="comfortable" @click="showDialog = false"></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-6">
                    <div class="mb-6">
                        <div class="text-subtitle-2 text-grey-darken-1 mb-1">파일명</div>
                        <div class="text-body-1 font-weight-medium text-indigo-darken-2 d-flex align-center">
                            <v-icon size="small" class="mr-2">mdi-file-outline</v-icon>
                            {{ selectedDocument?.fileName }}
                        </div>
                    </div>

                    <!-- Category Selection Section -->
                    <div class="mb-6 pa-4 bg-indigo-lighten-5 rounded-xl">
                        <div class="text-subtitle-2 font-weight-bold text-indigo-darken-4 mb-3 d-flex align-center">
                            <v-icon size="small" class="mr-2">mdi-tag-multiple</v-icon>
                            문서 분류 변경
                        </div>
                        <v-row dense>
                            <v-col cols="4">
                                <v-select v-model="selectedL1" :items="l1List" item-title="catNm" item-value="catId"
                                    label="대분류" variant="solo" flat density="comfortable" hide-details rounded="lg"
                                    @update:model-value="selectedL2 = null; selectedL3 = null"
                                    placeholder="대분류 선택"></v-select>
                            </v-col>
                            <v-col cols="4">
                                <v-select v-model="selectedL2" :items="l2List" item-title="catNm" item-value="catId"
                                    label="중분류" variant="solo" flat density="comfortable" hide-details rounded="lg"
                                    :disabled="!selectedL1" @update:model-value="selectedL3 = null"
                                    placeholder="중분류 선택"></v-select>
                            </v-col>
                            <v-col cols="4">
                                <v-select v-model="selectedL3" :items="l3List" item-title="catNm" item-value="catId"
                                    label="소분류" variant="solo" flat density="comfortable" hide-details rounded="lg"
                                    :disabled="!selectedL2" placeholder="소분류 선택"></v-select>
                            </v-col>
                        </v-row>
                    </div>

                    <div>
                        <div class="d-flex justify-space-between align-center mb-2">
                            <div class="text-subtitle-2 text-grey-darken-1">문서 내용 <span
                                    class="text-caption text-grey ml-1">(총
                                    {{ documentContent.length }} 문단)</span></div>
                            <div class="text-caption text-indigo font-weight-bold">
                                {{ currentPage }} / {{ totalPages }} 페이지
                            </div>
                        </div>

                        <div class="bg-grey-lighten-5 pa-4 rounded-lg border d-flex flex-column" style="height: 600px;">
                            <div class="flex-grow-1 overflow-y-auto pr-2 custom-scrollbar">
                                <div v-for="(paragraph, index) in paginatedContent" :key="index" class="mb-4">
                                    <div class="text-caption text-indigo-darken-1 mb-1 font-weight-bold">
                                        문단 {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                                    </div>
                                    <v-sheet class="pa-4 bg-white rounded-lg border-sm text-body-2 text-grey-darken-3"
                                        elevation="0" style="line-height: 1.7;">
                                        {{ paragraph }}
                                    </v-sheet>
                                </div>
                                <div v-if="paginatedContent.length === 0"
                                    class="d-flex align-center justify-center h-100 text-grey">
                                    내용이 없습니다.
                                </div>
                            </div>

                            <div class="pt-4 border-top mt-2">
                                <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7"
                                    density="compact" active-color="indigo-darken-2" variant="flat"
                                    class="mb-0"></v-pagination>
                            </div>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="pa-4 bg-grey-lighten-5">
                    <v-spacer></v-spacer>
                    <v-btn color="grey-darken-1" variant="text" @click="showDialog = false" class="px-4">닫기</v-btn>
                    <v-btn color="indigo-darken-2" variant="flat" @click="updateCategory" :loading="loading"
                        class="px-6 rounded-lg">분류 저장</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from '@/plugins/axios';
import { loadModelSettings } from '@/utils/modelSettings';

const files = ref([]);
const uploading = ref(false);
const loading = ref(false);
const search = ref('');
const isDragging = ref(false);
const documents = ref([]);
const titleSlot = 'item.doc_title';
const actionSlot = 'item.actions';
const showDialog = ref(false);
const showUploadDialog = ref(false);
const selectedDocument = ref(null);
const documentContent = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(10);

const categories = ref([]);
const selectedL1 = ref(null);
const selectedL2 = ref(null);
const selectedL3 = ref(null);
const uploadCosSim = ref(0.600);
const isSimValid = ref(true);

const defaultProvider = ref(null);
const showHybridOption = computed(() => defaultProvider.value !== null && defaultProvider.value !== 'ollama');

const useHybrid = ref(false);

const l1List = computed(() => categories.value.filter(c => c.catLvl === 1));
const l2List = computed(() => categories.value.filter(c => c.catLvl === 2 && c.upperCatId === selectedL1.value));
const l3List = computed(() => categories.value.filter(c => c.catLvl === 3 && c.upperCatId === selectedL2.value));

const fetchCategories = async () => {
    try {
        const res = await axios.post('/rag/category/list', {});
        categories.value = res.data.data.list;
    } catch (err) {
        console.error('Failed to fetch categories:', err);
    }
};

const getCategoryName = (catId) => {
    if (!catId) return '-';
    const cat = categories.value.find(c => String(c.catId) === String(catId));
    return cat ? cat.catNm : '-';
};

const totalPages = computed(() => Math.ceil(documentContent.value.length / itemsPerPage.value));

const paginatedContent = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return documentContent.value.slice(start, end);
});

// Watch for dialog open to reset scroll or page if needed
watch(showDialog, (val) => {
    if (!val) {
        // Optional: clear content or reset state on close
    }
});

// Watch for upload dialog open to reset default settings
watch(showUploadDialog, (val) => {
    if (val) {
        uploadCosSim.value = 0.600;
        useHybrid.value = false;
        files.value = [];
    }
});

const headers = [
    { title: '문서 ID', key: 'doc_id', align: 'start', width: '10%', sortable: true },
    { title: '분류', key: 'cat_id', align: 'start', width: '20%', sortable: true },
    { title: '문서 제목', key: 'doc_title', align: 'start', width: '40%', sortable: true },
    { title: '유사도 기준', key: 'cos_sim', align: 'center', width: '10%', sortable: true },
    { title: '관리', key: 'actions', align: 'end', width: '20%', sortable: false },
];

const handleFileUpload = () => {
    // Handle file selection if needed immediately
};

const handleDrop = (e) => {
    isDragging.value = false;
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
        // Filter for PDF only if needed, or just add them
        const pdfs = Array.from(droppedFiles).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
        if (pdfs.length > 0) {
            files.value = [...files.value, ...pdfs];
        } else {
            alert('PDF 파일만 업로드 가능합니다.');
        }
    }
};

const fetchDocuments = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/rag/docs/list');
        if (response.data.status === 'success') {
            documents.value = response.data.data.list;
        }
    } catch (error) {
        console.error('Failed to fetch documents:', error);
    } finally {
        loading.value = false;
    }
};

const uploadFiles = async () => {
    if (files.value.length === 0) return;

    uploading.value = true;
    try {
        for (const file of files.value) {
            const formData = new FormData();
            formData.append('file', file);
            if (selectedL3.value || selectedL2.value || selectedL1.value) {
                formData.append('catId', selectedL3.value || selectedL2.value || selectedL1.value);
            }
            formData.append('cosSim', uploadCosSim.value);
            
            // LLM Provider가 ollama이면 무조건 하이브리드를 비활성화하여 전송
            const actualUseHybrid = showHybridOption.value ? useHybrid.value : false;
            formData.append('useHybrid', actualUseHybrid);

            const response = await axios.post('/rag/docs/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            if (response.data.status !== 'success') {
                alert(`문서 처리 실패 (${file.name}): ${response.data.message}`);
                continue;
            }
        }
        await fetchDocuments();
        files.value = [];
        uploadCosSim.value = 0.600; // Reset to default
        useHybrid.value = false; // Reset to default false
        showUploadDialog.value = false;
    } catch (error) {
        console.error('Failed to upload files:', error);
        alert('문서 업로드 중 오류가 발생했습니다.');
    } finally {
        uploading.value = false;
    }
};

const deleteDoc = async (doc) => {
    if (!confirm(`정말 '${doc.doc_title}' 문서를 삭제하시겠습니까?`)) return;

    loading.value = true;
    try {
        await axios.delete(`/rag/docs/${encodeURIComponent(doc.doc_title)}`);
        await fetchDocuments();
    } catch (error) {
        console.error('Failed to delete document:', error);
        alert('문서 삭제 중 오류가 발생했습니다.');
    } finally {
        loading.value = false;
    }
};

const openDocument = async (doc) => {
    selectedDocument.value = { title: doc.doc_title, fileName: doc.doc_title, catId: doc.cat_id };

    // Reset and Initialize categories
    selectedL1.value = null;
    selectedL2.value = null;
    selectedL3.value = null;

    if (doc.cat_id) {
        const cat = categories.value.find(c => String(c.catId) === String(doc.cat_id));
        if (cat) {
            if (cat.catLvl === 1) {
                selectedL1.value = cat.catId;
            } else if (cat.catLvl === 2) {
                selectedL1.value = cat.upperCatId;
                selectedL2.value = cat.catId;
            } else if (cat.catLvl === 3) {
                const parentCat = categories.value.find(c => String(c.catId) === String(cat.upperCatId));
                if (parentCat) {
                    selectedL1.value = parentCat.upperCatId;
                    selectedL2.value = parentCat.catId;
                }
                selectedL3.value = cat.catId;
            }
        }
    }

    currentPage.value = 1;
    loading.value = true;
    try {
        const response = await axios.get(`/rag/docs/chunks/${encodeURIComponent(doc.doc_title)}`);
        if (response.data.status === 'success') {
            documentContent.value = response.data.data.chunks.map(c => c.content);
            showDialog.value = true;
        }
    } catch (error) {
        console.error('Failed to fetch chunks:', error);
        alert('문서 내용을 불러오는 중 오류가 발생했습니다.');
    } finally {
        loading.value = false;
    }
};

const updateCategory = async () => {
    if (!selectedDocument.value) return;

    const catId = selectedL3.value || selectedL2.value || selectedL1.value;
    if (!catId) {
        alert('분류를 선택해주세요.');
        return;
    }

    loading.value = true;
    try {
        const response = await axios.put(`/rag/docs/${encodeURIComponent(selectedDocument.value.title)}/category`, {
            catId: catId
        });
        if (response.data.status === 'success') {
            await fetchDocuments();
            showDialog.value = false;
        } else {
            alert('분류 수정 실패: ' + response.data.message);
        }
    } catch (error) {
        console.error('Failed to update category:', error);
        alert('분류 수정 중 오류가 발생했습니다.');
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    loadModelSettings(axios).then(settings => {
        defaultProvider.value = settings.models.find(model => model.value === settings.defaultModel)?.provider ?? null;
    }).catch(error => console.error('모델 설정을 불러오지 못했습니다.', error));
    fetchDocuments();
    fetchCategories();
});
</script>

<style scoped>
.border-dashed {
    border-style: dashed !important;
}

.shadow-soft {
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05) !important;
}

.modern-table :deep(thead tr th) {
    background-color: #283593 !important;
    color: #FFFFFF !important;
    font-weight: 700 !important;
    font-size: 0.85rem !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    height: 56px !important;
}

.modern-table :deep(tbody tr:nth-of-type(2n)) {
    background-color: #F0F2F5 !important;
}

.modern-table :deep(tbody tr:hover) {
    background-color: #E3F2FD !important;
}

.modern-table :deep(tbody tr td) {
    padding-top: 16px !important;
    padding-bottom: 16px !important;
    border-bottom: none !important;
}

.search-field :deep(.v-field__outline__start) {
    border-radius: 12px 0 0 12px !important;
}

.search-field :deep(.v-field__outline__end) {
    border-radius: 0 12px 12px 0 !important;
}

.custom-scrollbar::-webkit-scrollbar {
    width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #c5cae9;
    border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #9fa8da;
}

/* Hide spin buttons for number input */
.no-spin :deep(input::-webkit-outer-spin-button),
.no-spin :deep(input::-webkit-inner-spin-button) {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
}

.no-spin :deep(input[type=number]) {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>

<style>
/* 툴팁 다크 배경 (content-class) */
.help-tooltip-dark {
    background: rgba(55, 65, 81, 0.96) !important;
    color: rgba(255, 255, 255, 0.92);
}
</style>
